import type { Difficulty, PoolCategory } from '../shared/index.js';

export const s = {
  name: 'Şarkıyı Bil',
  pitch: 'Şarkıyı 0,1 saniyeden bil',
  categories: {
    turkce: 'Türkçe',
    global: 'Global',
    tur: 'Türler',
    donem: 'Dönemler',
    ulke: 'Ülke listeleri',
    ozel: 'Eklediğin listeler',
  } satisfies Record<PoolCategory, string>,
  difficulty: {
    easy: 'Kolay',
    medium: 'Orta',
    hard: 'Zor',
    expert: 'Uzman',
    impossible: 'İmkânsız',
  } satisfies Record<Difficulty, string>,
  difficultyHint: {
    easy: 'Listelerin en çok dinlenen şarkıları',
    medium: 'Bilinen şarkılar ağırlıkta',
    hard: 'Orta popülerlikte şarkılar',
    expert: 'Daha az dinlenmiş şarkılar',
    impossible: 'Listelerin en az bilinenleri',
  } satisfies Record<Difficulty, string>,
  settings: {
    pools: 'Liste',
    poolsLoading: 'Listeler hazırlanıyor. İlk açılışta bir dakika sürebilir.',
    openCatalog: 'Liste ekle / çıkar',
    closeCatalog: 'Kapat',
    addPool: 'Deezer ya da Spotify playlist linki',
    removeMine: (name: string) => `${name} listesini listelerimden çıkar`,
    mineHint: 'Eklediğin listeler yalnızca sana görünür.',
    addPoolPlaceholder: 'open.spotify.com/playlist/…',
    addingSpotify: 'Spotify listesi Deezer’da eşleştiriliyor…',
    addedSpotify: (name: string, n: number, total: number) => `${name} eklendi ve seçildi: ${total} şarkının ${n} tanesi bulundu.`,
    addPoolButton: 'Ekle',
    adding: 'Ekleniyor…',
    added: (name: string, n: number) => `${name} eklendi ve seçildi (${n} şarkı).`,
    difficulty: 'Zorluk',
    rounds: 'Tur sayısı',
    unlimited: 'Sınırsız',
    unlimitedHint: 'Oda sahibi bitirene kadar şarkı şarkı devam eder.',
    timer: 'Süre',
    timerOff: 'Süresiz',
    timerSec: (n: number) => `${n} sn`,
    timerHintOff: 'Bilmeyen herkes pas deyince ya da yanlış tahmin edince sonraki parça açılır.',
    timerHintOn: (n: number) => `Her parça için ${n} saniye. Herkes pas derse daha erken açılır.`,
    playback: 'Oynatma',
    startRandom: 'Rastgele bir yerden',
    startBeginning: 'Önizlemenin başından',
    search: 'Arama',
    easySearch: 'Kolay arama',
    easySearchHint: 'Öneriler yalnızca seçili listelerden gelir.',
    guessAfter: 'Tahmin şuradan başlasın',
    artist: 'Sanatçıyı bilene puan',
    artistHint: 'Sadece sanatçıyı bilen puanın %30’unu alır, şarkıyı aramaya devam eder.',
    readOnly: 'Ayarları oda sahibi seçer.',
    liveHint: 'Değişiklikler sıradaki şarkıdan itibaren geçerli olur.',
    options: 'Seçenekler',
    volume: 'Ses',
    on: 'Açık',
    off: 'Kapalı',
  },
  play: {
    round: (r: number, total: number | null) => (total ? `Tur ${r}/${total}` : `Şarkı ${r}`),
    countdown: 'Hazır ol',
    playLabel: (clip: string) => `${clip} klibi çal`,
    stopLabel: 'Durdur',
    tapToListen: 'Dinlemek için dokun',
    stageHint: (seconds: number) => `Sadece ${seconds.toLocaleString('tr-TR')} saniye! Kulağını aç.`,
    scoreYou: 'Sen',
    searchPlaceholder: 'Şarkıyı bul',
    guessLabel: 'Tahminin',
    noHits: 'Sonuç yok. Başka bir yazımla dene.',
    searching: 'Aranıyor…',
    skip: 'Pas',
    giveUp: 'Pes et',
    advance: 'Sonraki parçayı herkese aç',
    replayHint: 'Beklerken açılmış parçalara çubuktan dokunup tekrar dinleyebilirsin.',
    waiting: (n: number) => (n === 1 ? '1 kişi bekleniyor' : `${n} kişi bekleniyor`),
    states: {
      locked: 'Yanlış. Diğerleri pas deyince sonraki parça açılır.',
      skipped: 'Pas geçtin. Diğerleri bekleniyor.',
      correct: 'Bildin! Diğerleri bekleniyor.',
      artist: 'Sanatçı doğru, şimdi şarkının adını bul.',
      listening: '',
    },
    verdict: {
      wrong: (t: string) => `${t} değil.`,
      artist: (t: string) => `Sanatçı doğru, ama ${t} değil.`,
      correct: 'Doğru!',
    },
    loading: 'Sıradaki şarkı hazırlanıyor…',
    streak: 'Seri',
    best: 'En iyi',
    scores: 'Skor',
  },
  reveal: {
    itWas: 'Şarkı buydu',
    fromPool: (name: string) => `Liste: ${name}`,
    won: 'Bildin!',
    lost: 'Bilemedin',
    nobody: 'Bu turda kimse bilemedi.',
    atStage: (clip: string) => `${clip}’de`,
    artistOnly: 'sanatçıyı bildi',
    listen: 'Deezer’da dinle',
    replay: 'Önizlemeyi çal',
    stop: 'Durdur',
    next: 'Sonraki şarkı',
    finish: 'Sonuçları gör',
    waitingOthers: (ready: number, total: number) => `${ready}/${total} kişi hazır`,
    skipWait: 'Beklemeden geç',
    autoNext: (n: number) => `${n} sn içinde sıradaki şarkı`,
  },
  feed: {
    title: 'Canlı akış',
    you: 'Sen',
    text: (kind: string, clip: string, extra?: string) =>
      ({
        correct: `${clip}’de bildi`,
        artist: `${clip}’de sanatçıyı bildi`,
        wrong: 'yanlış tahmin etti',
        skip: 'pas geçti',
        settings: `listeyi değiştirdi: ${extra ?? ''}. Sıradaki şarkıdan itibaren geçerli.`,
      })[kind] ?? '',
  },
  howto: {
    title: 'Nasıl oynanır',
    steps: [
      ['Dinle', 'Şarkı 0,1 saniyelik bir parçayla başlar. Çal butonuyla parçayı istediğin kadar tekrar dinle.'],
      ['Tahmin et', 'Şarkının ya da sanatçının adını yaz, önerilerden doğru şarkıyı seç. Her parçada bir tahmin hakkın var.'],
      ['Pas geç ya da bekle', 'Bilmeyen herkes pas deyince ya da yanlış tahmin edince sonraki, daha uzun parça herkese açılır: 0,5 sn, 2 sn, 8 sn, 15 sn.'],
      ['Puanı topla', 'Ne kadar kısa parçada ve ne kadar hızlı bilirsen o kadar çok puan alırsın. Art arda bildiklerin serini büyütür.'],
    ] as [string, string][],
  },
  podium: {
    title: 'Oyun bitti',
    winner: (nick: string) => `${nick} kazandı!`,
    draw: 'Berabere!',
    points: (n: number) => `${n.toLocaleString('tr-TR')} puan`,
    streak: (n: number) => `En uzun seri: ${n}`,
    back: 'Lobiye dönülüyor…',
  },
};

/** 0.1 → "0,1 sn" */
export function clipLabel(seconds: number): string {
  return `${seconds.toLocaleString('tr-TR')} sn`;
}

/** Songspot'taki gibi kısa etiket: 0.1 → "0.1s" */
export function clipShort(seconds: number): string {
  return `${seconds}s`;
}
